"use client";

import { useSettingsStore } from "@/store/settingsStore";
import { ShoppingCart } from "lucide-react";
import { useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import { io } from "socket.io-client";

const backendUrl = process.env.NEXT_PUBLIC_API_URL
   ? process.env.NEXT_PUBLIC_API_URL.replace("/api", "")
   : "http://localhost:4000";

export function NotificationListener() {
   const router = useRouter();
   const { soundEnabled, notificationsEnabled } = useSettingsStore();
   const [notification, setNotification] = useState<{
      orderId?: string;
      title: string;
      message: string;
   } | null>(null);

   useEffect(() => {
      // Request browser permission
      if (
         typeof Notification !== "undefined" &&
         Notification.permission === "default"
      ) {
         Notification.requestPermission();
      }

      const socket = io(backendUrl);

      socket.on("connect", () => {
         console.log("Connected to socket", socket.id);
         socket.emit("join_admin");
      });

      socket.on("new_order", order => {
         console.log("New Order Received:", order);

         // 0. Dispatch custom DOM event so active pages update instantly
         if (typeof window !== "undefined") {
            window.dispatchEvent(
               new CustomEvent("new_order", { detail: order })
            );
         }

         // 1. Browser Notification
         if (notificationsEnabled) {
            if (
               typeof Notification !== "undefined" &&
               Notification.permission === "granted"
            ) {
               const notif = new Notification("Новый заказ! 🐟", {
                  body: `Заказ #${order.orderNumber} на ${Math.round(order.estimatedTotal)}₽\n${order.clientName}`,
               });
               notif.onclick = () => {
                  window.focus();
                  if (order?.id) {
                     window.location.href = `/orders?orderId=${order.id}`;
                  } else {
                     window.location.href = "/orders";
                  }
               };
            }
         }

         // 2. Sound
         if (soundEnabled) {
            try {
               // Using a pleasant notification sound
               const audio = new Audio(
                  "https://assets.mixkit.co/active_storage/sfx/2869/2869-preview.mp3",
               );
               audio.play().catch(e => console.error("Audio play failed", e));
            } catch (e) {
               console.error("Audio error", e);
            }
         }

         // 3. In-app Popup
         setNotification({
            orderId: order?.id,
            title: `Новый заказ #${order.orderNumber}`,
            message: `${order.clientName} • ~${Math.round(order.estimatedTotal)} ₽`,
         });

         // Auto hide popup
         setTimeout(() => setNotification(null), 8000);
      });

      return () => {
         socket.disconnect();
      };
   }, [soundEnabled, notificationsEnabled]);

   if (!notification) return null;

   return (
      <div className="fixed top-4 right-4 z-50 animate-in slide-in-from-right fade-in duration-300 pointer-events-none">
         <div
            className="bg-[hsl(var(--card))] border border-[hsl(var(--primary))] shadow-2xl rounded-2xl p-4 flex items-center gap-4 max-w-sm pointer-events-auto cursor-pointer"
            onClick={() => {
               if (notification.orderId) {
                  router.push(`/orders?orderId=${notification.orderId}`);
               } else {
                  router.push("/orders");
               }
               setNotification(null);
            }}
         >
            <div className="w-12 h-12 rounded-xl gradient-primary flex items-center justify-center flex-shrink-0 shadow-lg">
               <ShoppingCart className="text-white" size={24} />
            </div>
            <div className="min-w-0">
               <h3 className="font-bold text-lg leading-tight mb-1">
                  {notification.title}
               </h3>
               <p className="text-[hsl(var(--muted-foreground))] text-sm truncate">
                  {notification.message}
               </p>
            </div>
         </div>
      </div>
   );
}
